/**
 * Workout activity-log result content.
 *
 * Web counterpart of the native ActivityLogResultModalWorkoutContent: a summary
 * (duration / break / calories / weight) followed by the completed exercise
 * blocks and their sets. The activity log carries the workout log as raw JSON,
 * so it is hydrated via createWorkoutLog to expose the computed getters and
 * ExerciseBlockLog / ExerciseSet instances.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ActivityLog } from '../../../Model/Activity/ActivityLog';
import { createWorkoutLog } from '../../../Model/WorkoutLog/WorkoutLog';
import { ExerciseSet } from '../../../Model/WorkoutLog/ExerciseSet';
import { BaseTrackingKey } from '../../../Model/ProgramPortfolio/TrackingKeys';
import { getMediaLargeOrMediumOrSmallest } from '../../../Model/Media/Media';
import { EndWorkoutEditResultsTrackingKeys } from '../../../Components/EntryView/ExploreContentViewWorkoutTemplate/ExerciseRun/EndWorkoutEditResultsTrackingKeys';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useLanguage } from '../../../Hooks/useLanguage';
import { Theme } from '../../../Theme/Theme';

dayjs.extend(utc);

const SectionTitle = styled.div`
  margin-top: 24px;
  margin-bottom: 12px;
  color: ${Theme.Colors.charcoal};
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const SummaryCard = styled.div`
  padding: 16px;
  background: ${Theme.Colors.white};
  border-radius: 12px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
`;

const SummaryLabel = styled.div`
  color: ${Theme.Colors.charcoal};
  font-size: 12px;
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
`;

const BlockRow = styled.div`
  display: flex;
  gap: 16px;
  padding-bottom: 8px;
`;

const BlockMarker = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
`;

const BlockMarkerIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 24px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  & > i {
    color: ${Theme.Colors.primaryDarkest};
    font-size: 28px;
  }
`;

const BlockConnector = styled.div<{ $hidden?: boolean }>`
  flex: 1;
  width: 1px;
  background: ${(props) => (props.$hidden ? 'transparent' : Theme.Colors.lighterGrey)};
`;

const BlockBody = styled.div`
  flex: 1;
  padding-bottom: 24px;
  min-width: 0;
`;

const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BlockImage = styled.div<{ $imageUri?: string }>`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  flex-shrink: 0;
  background-image: ${(props) => (props.$imageUri ? `url(${props.$imageUri})` : 'none')};
  background-color: ${Theme.Colors.lightestGrey};
  background-size: cover;
  background-position: center;
`;

const BlockTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: ${Theme.Colors.textColor};
`;

const SetRow = styled.div`
  padding-top: 8px;
`;

const SetLabel = styled.div`
  font-size: 13px;
  color: ${Theme.Colors.charcoal};
`;

const SetValues = styled.div`
  color: ${Theme.Colors.textColor};
  font-size: 14px;
`;

function nonTimeTrackingKeys(set: ExerciseSet): BaseTrackingKey[] {
  const keys = Array.from(set.values.keys());
  const filtered = keys.filter(
    (key) => key !== 'DURATION' && key !== 'BREAK' && !key.startsWith('MIN_') && !key.startsWith('MAX_'),
  ) as BaseTrackingKey[];
  if (filtered.length > 0) return filtered;
  // Fall back to time-only sets (e.g. duration-based exercises)
  return keys.filter((key) => key !== 'BREAK' && !key.startsWith('MIN_') && !key.startsWith('MAX_')) as BaseTrackingKey[];
}

export type ActivityLogWorkoutContentProps = {
  activityLog: ActivityLog;
};

export const ActivityLogWorkoutContent: React.FC<ActivityLogWorkoutContentProps> = observer(({ activityLog }) => {
  const { t } = useTrans();
  const { language } = useLanguage();

  const workoutLog = React.useMemo(() => createWorkoutLog(activityLog.workoutLog as any), [activityLog.workoutLog]);

  const summary = [
    { key: 'DURATION', value: dayjs.utc(workoutLog.totalTimeExercising ?? 0).format('HH:mm:ss') },
    { key: 'BREAK', value: dayjs.utc(workoutLog.breakTime ?? 0).format('HH:mm:ss') },
    { key: 'CALORIES', value: `${Number(workoutLog.totalCaloriesBurnt ?? 0).toFixed(1)} kcal` },
    { key: 'WEIGHT', value: `${Number(workoutLog.tons ?? 0).toFixed(2)} t` },
  ];

  const blocks = workoutLog.exerciseBlocks;

  return (
    <div>
      <SectionTitle>{t('globals.statistics', { defaultValue: 'Statistics' })}</SectionTitle>
      <SummaryGrid>
        {summary.map((item) => (
          <SummaryCard key={item.key}>
            <SummaryLabel>{t(`trackingKeys.${item.key}`, { defaultValue: item.key })}</SummaryLabel>
            <SummaryValue>{item.value}</SummaryValue>
          </SummaryCard>
        ))}
      </SummaryGrid>

      <SectionTitle>{t('analytics.exercises', { defaultValue: 'Exercises' })}</SectionTitle>
      {blocks.map((block, blockIndex) => {
        const imageUri = block.exercise.imageMedia
          ? getMediaLargeOrMediumOrSmallest(block.exercise.imageMedia)
          : undefined;
        const isLastBlock = blockIndex === blocks.length - 1;
        return (
          <BlockRow key={`${block.exerciseBlockId ?? block.exercise.id}_${blockIndex}`}>
            <BlockMarker>
              <BlockMarkerIcon>
                <i className="mdi mdi-check" />
              </BlockMarkerIcon>
              <BlockConnector $hidden={isLastBlock} />
            </BlockMarker>
            <BlockBody>
              <BlockHeader>
                <BlockImage $imageUri={imageUri} />
                <BlockTitle>{block.exercise.localeName(language)}</BlockTitle>
              </BlockHeader>
              {block.sets.map((set, setIndex) => {
                const trackingKeys = nonTimeTrackingKeys(set);
                return (
                  <SetRow key={set.setId}>
                    <SetLabel>
                      {t('analytics.setIndex', { defaultValue: 'Set {{value}}', value: (set.index ?? setIndex) + 1 })}
                    </SetLabel>
                    <SetValues>
                      {trackingKeys.map((key, index, self) => (
                        <EndWorkoutEditResultsTrackingKeys
                          key={key}
                          set={set}
                          trackingKey={key}
                          isLast={index === self.length - 1}
                        />
                      ))}
                    </SetValues>
                  </SetRow>
                );
              })}
            </BlockBody>
          </BlockRow>
        );
      })}
    </div>
  );
});
